import React, { Fragment, useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Input,
  Pagination,
  PaginationFirst,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
  PaginationItems,
} from "components/ui";
import {
  ArrowUpTrayIcon,
  ArrowRightIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FolderIcon,
  FolderPlusIcon,
  ArrowUturnLeftIcon,
  ChevronRightIcon,
  HomeIcon,
  ClockIcon,
  Squares2X2Icon,
  Bars4Icon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import {
  Transition,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { toast } from "sonner";
import clsx from "clsx";
import { useDebounceCallback } from "hooks";

import {
  DOCUMENT_ENTITY_TYPES,
  setDefaultDocumentFolder,
  downloadDocumentFolderZip,
} from "services/documentFolderService";
import MoveItemModal from "./components/MoveItemModal";
import {
  verifyOrRejectLeadDocument,
  getLeadDocumentPreviewUrl,
} from "services/leadsService";
import {
  downloadJobDocument,
  deleteJobDocument,
  getJobDocumentPreviewUrl,
} from "services/jobsService";
import { getAttachmentPreviewUrl } from "services/documentUploadService";
import { useDocumentContext } from "app/contexts/DocumentContext/context";
import { invalidateTimelineCache } from "hooks/useTimeline";

import { useUnifiedDocuments } from "./hooks/useUnifiedDocuments";
import DocumentsFilter from "components/leads/LeadDetails/DocumentsTab/components/DocumentsFilter";
import {
  NoResultsFound,
  LoadingState,
  EmptyFolderState,
} from "components/leads/LeadDetails/DocumentsTab/components/DocumentEmptyStates";
import FolderCard from "components/leads/LeadDetails/DocumentsTab/components/FolderCard";
import {
  CreateFolderModal,
  RenameFolderModal,
  DeleteFolderModal,
} from "components/leads/LeadDetails/DocumentsTab/components/FolderCrudModals";
import RenameDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/RenameDocumentModal";
import ViewDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal";
import { UploadDocumentModal } from "components/leads/LeadDetails/DocumentsTab/components/UploadDocumentModal";
import DocumentVerificationModal from "components/leads/LeadDetails/DocumentsTab/components/DocumentVerificationModal";
import { ConfirmModal } from "components/shared/ConfirmModal";
import {
  getDocumentUrl,
  downloadDocumentWithToast,
} from "components/leads/LeadDetails/DocumentsTab/utils/documentUtils";
import {
  getAdjustedPosition,
  GlobalTableContextMenu,
} from "custom-components/shared/TableActionMenu";
import DocumentCard from "components/leads/LeadDetails/DocumentsTab/components/DocumentCard";

/**
 * Helper to recursively parse dropped files & folder structures from DataTransferItems
 */
const parseDataTransferItems = async (dataTransfer) => {
  if (!dataTransfer) return [];
  const items = Array.from(dataTransfer.items || []);
  const fileList = [];

  const traverseEntry = async (entry, path = "") => {
    if (!entry) return;
    if (entry.isFile) {
      return new Promise((resolve) => {
        entry.file(
          (file) => {
            const relPath = path ? `${path}/${file.name}` : file.name;
            Object.defineProperty(file, "webkitRelativePath", {
              value: relPath,
              writable: true,
              configurable: true,
            });
            fileList.push(file);
            resolve();
          },
          () => resolve()
        );
      });
    } else if (entry.isDirectory) {
      const dirReader = entry.createReader();
      const readAllEntries = async () => {
        let batch = [];
        let resBatch = await new Promise((res) => dirReader.readEntries(res, () => res([])));
        while (resBatch && resBatch.length > 0) {
          batch = batch.concat(Array.from(resBatch));
          resBatch = await new Promise((res) => dirReader.readEntries(res, () => res([])));
        }
        const currentPath = path ? `${path}/${entry.name}` : entry.name;
        for (const child of batch) {
          await traverseEntry(child, currentPath);
        }
      };
      await readAllEntries();
    }
  };

  const promises = items.map((item) => {
    if (item.webkitGetAsEntry) {
      const entry = item.webkitGetAsEntry();
      if (entry) return traverseEntry(entry, "");
    }
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (file) fileList.push(file);
    }
    return Promise.resolve();
  });

  await Promise.all(promises);

  if (fileList.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
    return Array.from(dataTransfer.files);
  }

  return fileList;
};

/**
 * Unified DocumentManager component for Lead, Job, and Global Settings document management.
 */
export const DocumentManager = ({
  entityType = DOCUMENT_ENTITY_TYPES.LEAD,
  entityId = null,
  title = "Documents",
  description = null,
  isMobile = false,
  isStudent = false,
  IsFinalApproved = false,
  afterFinalApprove = () => { },
  refreshProgressJourney = () => { },
  onEntityUpdated = () => { },
  features = {
    enableVerification: false,
    enableAssignInspectors: false,
    enableSetDefaultFolder: false,
    enableHistoryView: false,
  },
  customActions = {},
  useDocumentsData: externalDocumentsData,
  readOnly = false,
  maxFileSize,
  maxFiles,
  additionalDocuments = [],
}) => {
  const activeEntityId = entityId;

  // Internal documents hook
  const internalDocumentsData = useUnifiedDocuments({
    entityType,
    entityId: externalDocumentsData ? null : activeEntityId,
  });

  const activeDocs = externalDocumentsData || internalDocumentsData;

  const {
    documents,
    loading,
    totalRecords,
    hasInitialData,
    hasActiveFilters,
    filterState,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    switchView,
    viewType,
    totalPages,
    refetch,
    folders,
    currentFolderId,
    parentFolderId,
    rootFolderId,
    folderPath,
    navigateToFolder,
    navigateToBreadcrumb,
    navigateUp,
  } = activeDocs;

  const { refreshCounts } = useDocumentContext();

  // Calculate active filter count for badge indicator
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filterState?.fileType && filterState.fileType.length > 0) count++;
    if (filterState?.startDate || filterState?.endDate) count++;
    if (filterState?.modifiedStartDate || filterState?.modifiedEndDate) count++;
    return count;
  }, [filterState]);

  // Modals & UI state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState("file"); // "file" | "folder"
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [targetFolderAction, setTargetFolderAction] = useState(null);
  const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
  const [isDeleteFolderOpen, setIsDeleteFolderOpen] = useState(false);

  // Direct Drag & Drop state
  const [isDraggingOverContainer, setIsDraggingOverContainer] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [targetUploadFolderId, setTargetUploadFolderId] = useState(null);
  const [targetUploadFolderName, setTargetUploadFolderName] = useState("");
  const containerDragCounter = React.useRef(0);

  const isDragDropDisabled = readOnly || isStudent || (IsFinalApproved && entityType === DOCUMENT_ENTITY_TYPES.LEAD);

  const handleContainerDragEnter = (e) => {
    if (isDragDropDisabled || isUploadModalOpen) return;
    e.preventDefault();
    e.stopPropagation();
    containerDragCounter.current += 1;
    if (containerDragCounter.current === 1) {
      setIsDraggingOverContainer(true);
    }
  };

  const handleContainerDragOver = (e) => {
    if (isDragDropDisabled || isUploadModalOpen) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleContainerDragLeave = (e) => {
    if (isDragDropDisabled || isUploadModalOpen) return;
    e.preventDefault();
    e.stopPropagation();
    containerDragCounter.current -= 1;
    if (containerDragCounter.current <= 0) {
      containerDragCounter.current = 0;
      setIsDraggingOverContainer(false);
    }
  };

  const processAndOpenUploadModal = async (dataTransfer, targetFolderObj = null) => {
    const extractedFiles = await parseDataTransferItems(dataTransfer);
    if (!extractedFiles || extractedFiles.length === 0) return;

    const destFolderId = targetFolderObj?.FolderId || targetFolderObj?.id || currentFolderId || rootFolderId;
    const destFolderName = targetFolderObj?.Name || targetFolderObj?.name || (
      folderPath && folderPath.length > 0 ? folderPath[folderPath.length - 1]?.name : "Root Folder"
    );

    const hasFolderStructure = extractedFiles.some((f) => f.webkitRelativePath && f.webkitRelativePath.includes("/"));
    setUploadType(hasFolderStructure ? "folder" : "file");
    setTargetUploadFolderId(destFolderId);
    setTargetUploadFolderName(destFolderName);
    setDroppedFiles((prev) => (isUploadModalOpen ? [...prev, ...extractedFiles] : extractedFiles));
    setIsUploadModalOpen(true);
  };

  const handleContainerDrop = async (e) => {
    if (isDragDropDisabled || isUploadModalOpen) return;
    e.preventDefault();
    e.stopPropagation();
    containerDragCounter.current = 0;
    setIsDraggingOverContainer(false);
    await processAndOpenUploadModal(e.dataTransfer, null);
  };

  const handleDropOnSpecificFolder = async (folder, dataTransfer) => {
    if (isDragDropDisabled || isUploadModalOpen) return;
    containerDragCounter.current = 0;
    setIsDraggingOverContainer(false);
    await processAndOpenUploadModal(dataTransfer, folder);
  };

  // File rename state
  const [isRenameDocOpen, setIsRenameDocOpen] = useState(false);
  const [renameDocTarget, setRenameDocTarget] = useState(null);

  // Search & Display mode state
  const [displayMode, setDisplayMode] = useState("list"); // "list" | "grid"
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounceCallback((val) => {
    const trimmed = (val || "").trim();
    if (trimmed.length > 0 && trimmed.length < 3) {
      handleFilterChange("search", "");
    } else {
      handleFilterChange("search", trimmed);
    }
  }, 300);

  useEffect(() => {
    debouncedSearch(searchTerm);
  }, [searchTerm, debouncedSearch]);

  // Combined and sorted items (Folders + Documents intermingled together by creation/upload date descending)
  const allItems = useMemo(() => {
    const getTimestamp = (dateVal) => {
      if (!dateVal) return 0;
      if (typeof dateVal === "number") return dateVal;
      const parsed = new Date(dateVal).getTime();
      return isNaN(parsed) ? 0 : parsed;
    };

    const folderItems = (folders || []).map((f) => ({
      ...f,
      isFolderItem: true,
      sortTimestamp: getTimestamp(f.CreatedOn || f.createdOn),
    }));

    // additionalDocuments (e.g. attachments captured on a JobData dynamic/custom field) aren't
    // real DocumentFolders rows and don't belong to any particular folder, so they're only ever
    // shown at the root/overview level - including them on every navigated-into subfolder as well
    // would render the same files again at each depth, looking like duplicates.
    const isAtRootView = !currentFolderId || currentFolderId === rootFolderId || !folderPath || folderPath.length <= 1;
    const docItems = [
      ...(documents || []),
      ...(isAtRootView ? additionalDocuments : []),
    ].map((d) => ({
      ...d,
      isFolderItem: false,
      sortTimestamp: getTimestamp(d.CreatedOn || d.createdOn || d.uploadedAt),
    }));

    return [...folderItems, ...docItems].sort((a, b) => b.sortTimestamp - a.sortTimestamp);
  }, [folders, documents, additionalDocuments, currentFolderId, rootFolderId]);

  // Preview & Delete document states
  const [activePreviewDoc, setActivePreviewDoc] = useState(null);
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);

  // Multi-selection state for Bulk Move / Bulk Actions
  const [selectedItemsMap, setSelectedItemsMap] = useState(new Map());

  const getItemKey = useCallback((item) => {
    if (!item) return "";
    const isFolder =
      item.isFolderItem !== undefined
        ? Boolean(item.isFolderItem)
        : Boolean(item.FolderId && !item.DocumentId);
    return isFolder ? `folder-${item.FolderId || item.id}` : `doc-${item.DocumentId || item.id}`;
  }, []);

  const toggleSelectItem = useCallback((item) => {
    const key = getItemKey(item);
    if (!key) return;
    setSelectedItemsMap((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, item);
      }
      return next;
    });
  }, [getItemKey]);

  const isAllSelected = useMemo(() => {
    if (!allItems || allItems.length === 0) return false;
    return allItems.every((item) => selectedItemsMap.has(getItemKey(item)));
  }, [allItems, selectedItemsMap, getItemKey]);

  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedItemsMap(new Map());
    } else {
      const next = new Map();
      allItems.forEach((item) => {
        const key = getItemKey(item);
        if (key) next.set(key, item);
      });
      setSelectedItemsMap(next);
    }
  }, [isAllSelected, allItems, getItemKey]);

  const clearSelection = useCallback(() => {
    setSelectedItemsMap(new Map());
  }, []);

  const selectedItemSummaryText = useMemo(() => {
    const selectedList = Array.from(selectedItemsMap.values());
    if (selectedList.length === 0) return "";
    let folders = 0;
    let files = 0;
    selectedList.forEach((item) => {
      const isFolder =
        item?.isFolderItem !== undefined
          ? Boolean(item.isFolderItem)
          : Boolean(item?.FolderId && !item?.DocumentId && !item?.filePath && !item?.FilePath);
      if (isFolder) folders++;
      else files++;
    });
    if (folders > 0 && files === 0) {
      return `${folders} ${folders === 1 ? "folder" : "folders"} selected`;
    }
    if (files > 0 && folders === 0) {
      return `${files} ${files === 1 ? "file" : "files"} selected`;
    }
    return `${folders} ${folders === 1 ? "folder" : "folders"} & ${files} ${files === 1 ? "file" : "files"} selected`;
  }, [selectedItemsMap]);

  // Clear selection when navigating folders
  useEffect(() => {
    setSelectedItemsMap(new Map());
  }, [currentFolderId]);

  // Move item state & Zip Download handler
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [itemsToMoveTarget, setItemsToMoveTarget] = useState([]);

  const handleOpenMoveModal = (itemsOrSingle) => {
    if (Array.isArray(itemsOrSingle) && itemsOrSingle.length > 0) {
      setItemsToMoveTarget(itemsOrSingle);
    } else if (itemsOrSingle && typeof itemsOrSingle === "object" && !Array.isArray(itemsOrSingle)) {
      setItemsToMoveTarget([itemsOrSingle]);
    } else if (selectedItemsMap.size > 0) {
      setItemsToMoveTarget(Array.from(selectedItemsMap.values()));
    } else {
      setItemsToMoveTarget([]);
    }
    setIsMoveModalOpen(true);
  };

  const handleFolderZipDownload = async (folder) => {
    if (!folder?.FolderId) return;
    const toastId = toast.loading(`Preparing ZIP for "${folder.Name}"...`);
    try {
      const res = await downloadDocumentFolderZip(folder.FolderId, folder.Name);
      if (res.success) {
        toast.success(res.message || "Folder ZIP download started.", { id: toastId });
      } else {
        toast.error(res.error || "Failed to download folder as ZIP", { id: toastId });
      }
    } catch (err) {
      toast.error("Error downloading folder as ZIP", { id: toastId });
    }
  };

  // Lead Verification States
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationAction, setVerificationAction] = useState("approve");
  const [verificationDocument, setVerificationDocument] = useState(null);
  const [isUpdatingVerification, setIsUpdatingVerification] = useState(false);

  // Helper default text
  const defaultSubtext =
    description ||
    (entityType === DOCUMENT_ENTITY_TYPES.LEAD
      ? `Total documents (${totalRecords ?? documents.length} items)`
      : entityType === DOCUMENT_ENTITY_TYPES.JOB
        ? `Manage and view job document folders and files (${totalRecords ?? documents.length} items)`
        : `Manage organization-wide template and default documents (${totalRecords ?? documents.length} items)`);

  // Container Right-Click handler (triggers general Add / Upload menu)
  const handleContainerContextMenu = (e) => {
    if (window.innerWidth < 768) return;
    if (IsFinalApproved && entityType === DOCUMENT_ENTITY_TYPES.LEAD) return;
    if (isStudent) return;
    if (readOnly) return;

    e.preventDefault();
    const pos = getAdjustedPosition(e.clientX, e.clientY);
    window.dispatchEvent(
      new CustomEvent("open-table-context-menu", {
        detail: {
          x: pos.left,
          y: pos.top,
          item: null,
          actions: {
            customActions: [
              {
                key: "new-folder",
                label: "New Folder",
                icon: FolderPlusIcon,
                iconColor: "text-amber-500",
                onClick: () => setIsCreateFolderOpen(true),
              },
              {
                key: "doc-upload",
                label: "Document Upload",
                icon: ArrowUpTrayIcon,
                iconColor: "text-blue-500",
                onClick: () => {
                  setDroppedFiles([]);
                  setUploadType("file");
                  setIsUploadModalOpen(true);
                },
              },
              {
                key: "folder-upload",
                label: "Folder Upload",
                icon: FolderIcon,
                iconColor: "text-amber-600",
                onClick: () => {
                  setDroppedFiles([]);
                  setUploadType("folder");
                  setIsUploadModalOpen(true);
                },
              },
            ],
          },
        },
      })
    );
  };

  // Default Folder handler
  const handleSetDefaultFolder = async (folder) => {
    if (!folder?.FolderId) return;
    const toastId = toast.loading(`Setting "${folder.Name}" as default folder...`);
    try {
      const res = await setDefaultDocumentFolder({ folderId: folder.FolderId });
      if (res.success) {
        toast.success(res.message || "Default folder updated successfully", { id: toastId });
        refetch(true);
      } else {
        toast.error(res.error || "Failed to set default folder", { id: toastId });
      }
    } catch (err) {
      console.error("Error setting default folder:", err);
      toast.error("Failed to set default folder", { id: toastId });
    }
  };

  // Document download handler
  const handleDownloadDoc = async (doc) => {
    if (customActions.onDownload) {
      return customActions.onDownload(doc);
    }
    return downloadDocumentWithToast(doc, { entityType });
  };

  // Preview URL handler
  const handleOpenInNewTab = async (doc) => {
    if (customActions.onOpenInNewTab) {
      return customActions.onOpenInNewTab(doc);
    }
    if (!doc) return;

    const docId = doc?.id || doc?.DocumentId;
    const filePathKey = doc?.FilePath || doc?.filePath || doc?.FileKey || doc?.fileKey || doc?.url || doc?.Url;

    if (filePathKey && typeof filePathKey === 'string' && (filePathKey.startsWith('http://') || filePathKey.startsWith('https://'))) {
      window.open(filePathKey, "_blank");
      return;
    }

    const lowerPath = typeof filePathKey === 'string' ? filePathKey.toLowerCase() : '';
    const isJobDoc = entityType === DOCUMENT_ENTITY_TYPES.JOB || doc?.isJobDocument || lowerPath.startsWith('job-documents/');
    const isLeadOrJobDoc = doc?.isLeadDocument || isJobDoc || lowerPath.startsWith('lead-documents/');
    const fetchPreviewUrl = isJobDoc ? getJobDocumentPreviewUrl : getLeadDocumentPreviewUrl;

    const toastId = toast.loading("Opening document...");
    try {
      let resultUrl = "";

      if (isLeadOrJobDoc && docId) {
        const result = await fetchPreviewUrl(docId);
        if (result.success && result.data?.url) {
          resultUrl = result.data.url;
        }
      } else if (filePathKey) {
        const result = await getAttachmentPreviewUrl(filePathKey);
        if (result.success && result.url) {
          resultUrl = result.url;
        }
      } else if (docId) {
        const result = await fetchPreviewUrl(docId);
        if (result.success && result.data?.url) {
          resultUrl = result.data.url;
        }
      }

      if (resultUrl) {
        toast.dismiss(toastId);
        const fileName = doc?.name || doc?.FileName || filePathKey || "";
        if (fileName.toLowerCase().endsWith(".csv")) {
          try {
            const response = await fetch(resultUrl);
            if (response.ok) {
              const text = await response.text();
              const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, "_blank");
              return;
            }
          } catch (e) {
            console.warn("Could not fetch CSV as blob, opening URL directly", e);
          }
        }
        window.open(resultUrl, "_blank");
        return;
      }

      const resolvedUrl = getDocumentUrl(filePathKey || doc?.url);
      if (resolvedUrl) {
        toast.dismiss(toastId);
        window.open(resolvedUrl, "_blank");
        return;
      }

      toast.error("Failed to open document preview URL", { id: toastId });
    } catch (err) {
      console.error("Error opening document in new tab:", err);
      toast.error("An error occurred while opening document", { id: toastId });
    }
  };

  // Document Delete Handlers
  const handleConfirmDeleteDoc = async () => {
    if (!deleteDocTarget) return;
    const docToDel = deleteDocTarget;
    setDeleteDocTarget(null);
    if (customActions.onDelete) {
      return customActions.onDelete(docToDel);
    }
    if (entityType === DOCUMENT_ENTITY_TYPES.JOB) {
      const toastId = toast.loading("Deleting document...");
      try {
        const res = await deleteJobDocument(docToDel.id);
        if (res?.success) {
          toast.success(res.message || "Document deleted successfully.", { id: toastId });
          refetch();
          if (onEntityUpdated) await onEntityUpdated(activeEntityId);
        } else {
          toast.error(res?.message || "Failed to delete document", { id: toastId });
        }
      } catch (err) {
        console.error("Error deleting document:", err);
        toast.error("An error occurred during deletion", { id: toastId });
      }
    } else {
      toast.success("Document deleted");
      refetch();
    }
  };

  // Lead Verification Handlers
  const handleOpenVerificationModal = (document, action) => {
    setVerificationDocument(document);
    setVerificationAction(action);
    setIsVerificationModalOpen(true);
  };

  const handleCloseVerificationModal = () => {
    if (isUpdatingVerification) return;
    setIsVerificationModalOpen(false);
    setVerificationDocument(null);
    setVerificationAction("approve");
  };

  const handleSubmitVerification = async ({ status, remark }) => {
    if (!verificationDocument?.id) {
      toast.error("Document not found for verification");
      return;
    }
    setIsUpdatingVerification(true);
    try {
      const result = await verifyOrRejectLeadDocument({
        id: verificationDocument.id,
        verificationStatus: status,
        remarks: remark?.trim() || undefined,
      });

      if (result.success) {
        toast.success(result.message || "Verification status updated successfully.");
        setIsVerificationModalOpen(false);
        setVerificationDocument(null);
        setVerificationAction("approve");
        await refetch();
        if (typeof refreshProgressJourney === "function") {
          await refreshProgressJourney();
        }
        if (typeof refreshCounts === "function") {
          await refreshCounts();
        }
      } else {
        toast.error(result.error || "Failed to update verification status");
      }
    } catch (error) {
      console.error("Verification update error:", error);
      toast.error(error?.message || "Failed to update verification status");
    } finally {
      setIsUpdatingVerification(false);
    }
  };

  return (
    <div
      className="space-y-2 min-h-0 flex-1 h-full flex flex-col relative overflow-hidden"
      onDragEnter={handleContainerDragEnter}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
    // onContextMenu={handleContainerContextMenu}
    >
      <GlobalTableContextMenu />

      {/* Drag & Drop Visual Overlay */}
      {isDraggingOverContainer && !isDragDropDisabled && (
        <div className="absolute -inset-2 z-50 bg-primary-500/10 dark:bg-primary-900/30 backdrop-blur-xs border-3 border-dashed border-primary-500 rounded-xl flex flex-col items-center justify-center p-6 transition-all animate-in fade-in duration-200 pointer-events-none">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-lg mb-3 animate-bounce">
            <ArrowUpTrayIcon className="h-8 w-8 stroke-[2]" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Drop files or folders here to upload
          </h3>
          <p className="text-xs font-semibold text-primary-700 dark:text-primary-300 mt-1.5 bg-white/90 dark:bg-dark-800 px-3.5 py-1 rounded-full border border-primary-200 dark:border-primary-800 shadow-sm">
            Target folder: <span className="font-bold text-amber-600 dark:text-amber-400">{targetUploadFolderName || (folderPath && folderPath.length > 0 ? folderPath[folderPath.length - 1]?.name : "Root Folder")}</span>
          </p>
        </div>
      )}

      {/* Header Chrome */}
      <div className="bg-white dark:bg-dark-800 rounded-lg p-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="min-w-max">
            <h2 className="dark:text-dark-50 text-xl font-semibold text-gray-900">
              {title}
            </h2>
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0">
            {/* Header Search Input */}
            <div className="relative w-44 sm:w-64">
              <MagnifyingGlassIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                className="pl-9 pr-8 text-xs py-1.5 h-9"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute top-1/2 right-2.5 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter Toggle Icon Button */}
            <button
              type="button"
              onClick={() => setIsFilterOpen((prev) => !prev)}
              className={clsx(
                "relative flex h-9 w-9 items-center justify-center rounded-lg border transition-colors cursor-pointer shrink-0",
                isFilterOpen || hasActiveFilters
                  ? "border-primary-500 bg-primary-50 text-primary-600 dark:border-primary-500/50 dark:bg-primary-900/30 dark:text-primary-400 shadow-xs"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:border-dark-500 dark:bg-dark-750 dark:text-dark-200 dark:hover:bg-dark-700 dark:hover:text-white"
              )}
              title={isFilterOpen ? "Hide filters" : "Show filters"}
            >
              <FunnelIcon className="h-4 w-4 stroke-[1.8]" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white shadow-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Top Actions Menu */}
            {(!IsFinalApproved || entityType !== DOCUMENT_ENTITY_TYPES.LEAD) && !readOnly && (
              <Menu as="div" className="relative inline-block text-left shrink-0">
                <MenuButton
                  as={Button}
                  variant="outlined"
                  color="primary"
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 h-9"
                >
                  <PlusIcon className="h-4 w-4 stroke-[2.5]" />
                  <span>New</span>
                </MenuButton>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 mt-2 min-w-[11.5rem] w-max origin-top-right rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-none focus:outline-none dark:border-dark-500 dark:bg-dark-750 dark:shadow-none z-50">
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={() => setIsCreateFolderOpen(true)}
                          disabled={isStudent}
                          className={clsx(
                            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer whitespace-nowrap",
                            focus && "bg-gray-100 text-gray-900 dark:bg-dark-600 dark:text-white",
                            isStudent && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <FolderPlusIcon className="size-4 stroke-[1.8] shrink-0 text-amber-500" />
                          <span className="whitespace-nowrap">New Folder</span>
                        </button>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={() => {
                            setDroppedFiles([]);
                            setUploadType("file");
                            setIsUploadModalOpen(true);
                          }}
                          disabled={isStudent}
                          className={clsx(
                            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer whitespace-nowrap",
                            focus && "bg-gray-100 text-gray-900 dark:bg-dark-600 dark:text-white",
                            isStudent && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <ArrowUpTrayIcon className="size-4 stroke-[1.8] shrink-0 text-blue-500" />
                          <span className="whitespace-nowrap">Document Upload</span>
                        </button>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ focus }) => (
                        <button
                          type="button"
                          onClick={() => {
                            setDroppedFiles([]);
                            setUploadType("folder");
                            setIsUploadModalOpen(true);
                          }}
                          disabled={isStudent}
                          className={clsx(
                            "flex h-9 w-full items-center gap-3 px-3 tracking-wide outline-hidden transition-colors text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-200 dark:hover:bg-dark-600 dark:hover:text-white cursor-pointer whitespace-nowrap",
                            focus && "bg-gray-100 text-gray-900 dark:bg-dark-600 dark:text-white",
                            isStudent && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <FolderIcon className="size-4 stroke-[1.8] shrink-0 text-amber-600" />
                          <span className="whitespace-nowrap">Folder Upload</span>
                        </button>
                      )}
                    </MenuItem>
                  </MenuItems>
                </Transition>
              </Menu>
            )}
          </div>
        </div>

        {/* Common Filter bar across all modules (Lead, Job, Global) */}
        {(isFilterOpen || hasActiveFilters) && (hasInitialData || documents?.length > 0) && (
          <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-dark-700 @container [container-type:inline-size]">
            <DocumentsFilter
              filterState={filterState}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
            />
          </div>
        )}
      </div>

      {/* Unified File Explorer Container */}
      {viewType === "documents" && (
        <div className="flex-1 min-h-0 h-full flex flex-col justify-between bg-white dark:bg-dark-800 rounded-lg p-2 sm:p-2.5 border border-gray-200/80 dark:border-dark-600 shadow-xs overflow-hidden">
          {/* Top Breadcrumb & Controls Bar */}
          <div className="flex items-center justify-between gap-3 pb-2 mb-1 border-b border-gray-100 dark:border-dark-700">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-0.2 flex-wrap min-w-0 cursor-pointer">
              {(folderPath || []).map((item, index) => {
                const isLast = index === (folderPath || []).length - 1;
                return (
                  <React.Fragment key={item.id || `root-${index}`}>
                    {index > 0 && (
                      <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400 dark:text-dark-400 shrink-0 mx-0.5" />
                    )}
                    {isLast ? (
                      <span className="inline-flex items-center gap-1.5 font-semibold text-gray-900 dark:text-dark-50 bg-primary-50/80 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-md text-xs sm:text-sm">
                        {index === 0 ? (
                          <HomeIcon className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400 shrink-0" />
                        ) : (
                          <FolderIcon className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                        )}
                        {item.name}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => navigateToBreadcrumb(index)}
                        className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium text-gray-600 dark:text-dark-300 hover:text-primary-600 dark:hover:text-primary-400 px-1.5 py-0.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                      >
                        {index === 0 && (
                          <HomeIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        )}
                        {item.name}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* View Mode Toggle & Back button */}
            <div className="flex items-center gap-2 shrink-0">
              {/* List / Grid Toggle */}
              <div className="flex items-center p-0.5 rounded-md bg-gray-100 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                <button
                  type="button"
                  onClick={() => setDisplayMode("list")}
                  className={clsx(
                    "p-1 rounded-sm transition-colors cursor-pointer",
                    displayMode === "list"
                      ? "bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400 shadow-xs"
                      : "text-gray-500 dark:text-dark-300 hover:text-gray-800 dark:hover:text-dark-100"
                  )}
                  title="List View"
                >
                  <Bars4Icon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDisplayMode("grid")}
                  className={clsx(
                    "p-1 rounded-sm transition-colors cursor-pointer",
                    displayMode === "grid"
                      ? "bg-white dark:bg-dark-800 text-primary-600 dark:text-primary-400 shadow-xs"
                      : "text-gray-500 dark:text-dark-300 hover:text-gray-800 dark:hover:text-dark-100"
                  )}
                  title="Grid View"
                >
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Back button */}
              {(parentFolderId || (folderPath && folderPath.length > 1)) && (
                <button
                  type="button"
                  onClick={navigateUp}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-700 dark:text-dark-200 bg-gray-100 hover:bg-gray-200 dark:bg-dark-700 dark:hover:bg-dark-600 px-2 py-1 rounded-md transition-colors shrink-0 cursor-pointer"
                >
                  <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                  <span>Back</span>
                </button>
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex-1">
              <LoadingState />
            </div>
          )}

          {/* Empty Search / Filters State */}
          {!loading && allItems.length === 0 && hasActiveFilters && (
            <div className="flex-1">
              <NoResultsFound isMobile={isMobile} />
            </div>
          )}

          {/* Empty Folder State */}
          {!loading && allItems.length === 0 && !hasActiveFilters && (
            <div className="flex-1 flex flex-col items-center justify-center">
              {folderPath && folderPath.length > 1 ? (
                <EmptyFolderState
                  isMobile={isMobile}
                  onUpload={() => {
                    setDroppedFiles([]);
                    setUploadType("file");
                    setIsUploadModalOpen(true);
                  }}
                  folderName={
                    folderPath && folderPath.length > 0
                      ? folderPath[folderPath.length - 1]?.name
                      : ""
                  }
                />
              ) : (
                <div className="text-center text-sm text-gray-500 dark:text-dark-300">
                  No items in root folder.
                </div>
              )}
            </div>
          )}

          {/* Bulk Selection Action Bar */}
          {selectedItemsMap.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded-lg bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 text-xs shadow-2xs">
              <div className="flex items-center gap-2 text-primary-900 dark:text-primary-100 font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[11px] font-bold text-white shrink-0">
                  {selectedItemsMap.size}
                </span>
                <span>{selectedItemSummaryText}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="xs"
                  color="primary"
                  onClick={() => handleOpenMoveModal(Array.from(selectedItemsMap.values()))}
                  className="flex items-center gap-1.5 font-semibold text-xs py-1 cursor-pointer"
                  disabled={readOnly}
                >
                  <ArrowRightIcon className="h-3.5 w-3.5" />
                  <span>Move To...</span>
                </Button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-gray-500 hover:text-gray-700 dark:text-dark-300 dark:hover:text-dark-100 text-xs font-medium px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {/* File Explorer Content List (Folders + Documents together) */}
          {!loading && allItems.length > 0 && (
            <div className="flex-1 flex flex-col justify-between min-h-0 overflow-hidden">
              {displayMode === "list" ? (
                <div className="overflow-auto rounded-lg border border-gray-200/80 bg-white p-2 dark:border-dark-700 dark:bg-dark-800 shadow-xs flex-1 min-h-0 relative">
                  <div className="min-w-[780px]">
                    {/* Single File Explorer Column Header Row */}
                    <div className="flex items-center justify-between px-2.5 py-1.5 text-xs font-semibold text-gray-500 dark:text-dark-300 border-b border-gray-200 dark:border-dark-600 mb-1">
                      <div className="flex items-center gap-2 flex-1 min-w-[180px] pr-3">
                        <div className="shrink-0 flex items-center pr-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            onChange={toggleSelectAll}
                            className="h-4 w-4 rounded border-gray-300 dark:border-dark-600 text-primary-600 focus:ring-primary-500 cursor-pointer"
                            title={isAllSelected ? "Deselect all" : "Select all"}
                          />
                        </div>
                        <span>Name</span>
                      </div>
                      <div className="w-40 shrink-0 pr-2">Uploaded</div>
                      <div className="w-40 shrink-0 pr-2">Modified</div>
                      <div className="w-28 shrink-0 pr-2">Type</div>
                      <div className="w-20 shrink-0 pr-2">Size</div>
                      <div className="w-12 shrink-0 text-right">Actions</div>
                    </div>

                    {/* List Items Container */}
                    <div className="space-y-0.5">
                      {allItems.map((item) => {
                        const itemKey = getItemKey(item);
                        const isSelected = selectedItemsMap.has(itemKey);

                        if (item.isFolderItem) {
                          return (
                            <FolderCard
                              key={`folder-${item.FolderId || item.id}`}
                              folder={item}
                              displayMode={displayMode}
                              isSelected={isSelected}
                              onToggleSelect={toggleSelectItem}
                              onClick={() => navigateToFolder(item.FolderId, item.Name)}
                              onDownloadZip={handleFolderZipDownload}
                              onMove={!readOnly ? handleOpenMoveModal : null}
                              onDropOnFolder={handleDropOnSpecificFolder}
                              readOnly={isDragDropDisabled}
                              onRename={
                                !IsFinalApproved && !isStudent && !readOnly
                                  ? (f) => {
                                    setTargetFolderAction(f);
                                    setIsRenameFolderOpen(true);
                                  }
                                  : null
                              }
                              onDelete={
                                !IsFinalApproved && !isStudent && !readOnly
                                  ? (f) => {
                                    setTargetFolderAction(f);
                                    setIsDeleteFolderOpen(true);
                                  }
                                  : null
                              }
                              onSetDefault={
                                features.enableSetDefaultFolder ? handleSetDefaultFolder : null
                              }
                            />
                          );
                        }

                        return (
                          <DocumentCard
                            key={`doc-${item.id || item.DocumentId}`}
                            document={item}
                            entityType={entityType}
                            displayMode={displayMode}
                            isSelected={isSelected}
                            onToggleSelect={toggleSelectItem}
                            refetch={refetch}
                            refreshProgressJourney={refreshProgressJourney}
                            onVerificationAction={handleOpenVerificationModal}
                            onMove={!readOnly ? handleOpenMoveModal : null}
                            onRename={!readOnly ? (d) => {
                              setRenameDocTarget(d);
                              setIsRenameDocOpen(true);
                            } : null}
                            isStudent={isStudent}
                            readOnly={readOnly}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* Grid Mode Items Container */
                <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3 overflow-auto flex-1 min-h-0 p-1">
                  {allItems.map((item) => {
                    const itemKey = getItemKey(item);
                    const isSelected = selectedItemsMap.has(itemKey);

                    if (item.isFolderItem) {
                      return (
                        <FolderCard
                          key={`folder-${item.FolderId || item.id}`}
                          folder={item}
                          displayMode={displayMode}
                          isSelected={isSelected}
                          onToggleSelect={toggleSelectItem}
                          onClick={() => navigateToFolder(item.FolderId, item.Name)}
                          onDownloadZip={handleFolderZipDownload}
                          onMove={!readOnly ? handleOpenMoveModal : null}
                          onDropOnFolder={handleDropOnSpecificFolder}
                          readOnly={isDragDropDisabled}
                          onRename={
                            !IsFinalApproved && !isStudent && !readOnly
                              ? (f) => {
                                setTargetFolderAction(f);
                                setIsRenameFolderOpen(true);
                              }
                              : null
                          }
                          onDelete={
                            !IsFinalApproved && !isStudent && !readOnly
                              ? (f) => {
                                setTargetFolderAction(f);
                                setIsDeleteFolderOpen(true);
                              }
                              : null
                          }
                          onSetDefault={
                            features.enableSetDefaultFolder ? handleSetDefaultFolder : null
                          }
                        />
                      );
                    }

                    return (
                      <DocumentCard
                        key={`doc-${item.id || item.DocumentId}`}
                        document={item}
                        entityType={entityType}
                        displayMode={displayMode}
                        isSelected={isSelected}
                        onToggleSelect={toggleSelectItem}
                        refetch={refetch}
                        refreshProgressJourney={refreshProgressJourney}
                        onVerificationAction={handleOpenVerificationModal}
                        onMove={!readOnly ? handleOpenMoveModal : null}
                        onRename={!readOnly ? (d) => {
                          setRenameDocTarget(d);
                          setIsRenameDocOpen(true);
                        } : null}
                        isStudent={isStudent}
                        readOnly={readOnly}
                      />
                    );
                  })}
                </div>
              )}
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center pt-2 border-t border-gray-100 dark:border-dark-700">
                  <Pagination
                    total={totalPages}
                    value={filterState.pageNumber}
                    onChange={handlePageChange}
                    className="pagination-primary"
                  >
                    <PaginationFirst className="pagination-first" />
                    <PaginationPrevious className="pagination-previous" />
                    <PaginationItems className="pagination-items" />
                    <PaginationNext className="pagination-next" />
                    <PaginationLast className="pagination-last" />
                  </Pagination>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History Empty View */}
      {viewType === "history" &&
        documents.length === 0 &&
        !loading &&
        !hasActiveFilters && (
          <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-12 text-center shadow-sm">
            <div className="mx-auto max-w-md">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
                <ClockIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No History Available
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Document verification history will appear here once documents are
                uploaded and verification actions are performed.
              </p>
              <Button
                variant="soft"
                color="primary"
                onClick={() => switchView("documents")}
                className="mx-auto"
              >
                Go to Documents
              </Button>
            </div>
          </div>
        )}

      {/* Modals */}
      <CreateFolderModal
        isOpen={isCreateFolderOpen}
        onClose={() => setIsCreateFolderOpen(false)}
        onCreateSuccess={() => refetch()}
        entityId={activeEntityId}
        parentFolderId={currentFolderId || rootFolderId}
        rootFolderId={rootFolderId}
        entityType={entityType}
      />

      <RenameFolderModal
        isOpen={isRenameFolderOpen}
        folder={targetFolderAction}
        onClose={() => {
          setIsRenameFolderOpen(false);
          setTargetFolderAction(null);
        }}
        onRenameSuccess={() => refetch()}
      />

      <DeleteFolderModal
        isOpen={isDeleteFolderOpen}
        folder={targetFolderAction}
        availableFolders={folders}
        entityType={entityType}
        entityId={activeEntityId}
        enableSetDefaultFolder={features.enableSetDefaultFolder}
        onClose={() => {
          setIsDeleteFolderOpen(false);
          setTargetFolderAction(null);
        }}
        onDeleteSuccess={() => refetch()}
      />

      <RenameDocumentModal
        isOpen={isRenameDocOpen}
        document={renameDocTarget}
        onClose={() => {
          setIsRenameDocOpen(false);
          setRenameDocTarget(null);
        }}
        onRenameSuccess={() => refetch()}
      />

      {/* Document Delete Confirmation Modal */}
      <ConfirmModal
        show={!!deleteDocTarget}
        onClose={() => setDeleteDocTarget(null)}
        onOk={handleConfirmDeleteDoc}
        state="pending"
        messages={{
          pending: {
            title: "Delete Document",
            description: `Are you sure you want to delete "${deleteDocTarget?.name}"? This action cannot be undone.`,
            actionText: "Delete",
          },
          success: {
            title: "Document Deleted",
            description: "The document has been successfully deleted.",
            actionText: "Done",
          },
          error: {
            title: "Delete Failed",
            description: "Failed to delete the document. Please try again.",
            actionText: "Retry",
          },
        }}
        buttonColor="error"
      />

      {/* Preview Modal */}
      {activePreviewDoc && (
        <ViewDocumentModal
          isOpen={!!activePreviewDoc}
          onClose={() => setActivePreviewDoc(null)}
          document={activePreviewDoc}
          onDownload={handleDownloadDoc}
          onOpenInNewTab={handleOpenInNewTab}
        />
      )}

      {/* Upload Document / Folder Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setDroppedFiles([]);
          setTargetUploadFolderId(null);
          setTargetUploadFolderName("");
        }}
        leadId={entityType === DOCUMENT_ENTITY_TYPES.LEAD ? activeEntityId : undefined}
        jobId={entityType === DOCUMENT_ENTITY_TYPES.JOB ? activeEntityId : undefined}
        entityType={entityType}
        uploadType={uploadType}
        initialFiles={droppedFiles}
        // At the true root (no subfolder navigated into), currentFolderId === rootFolderId -
        // the entity's physical Root folder. Sending that explicit id to the upload API bypasses
        // the backend's default-folder resolution (which only kicks in when folderId is null/empty),
        // so files land loose directly under Root instead of the entity's default folder (e.g. "Sales"
        // after a lead conversion, or "General" otherwise) - visually duplicating whatever already
        // lives one level down in that default folder. Only pass an explicit folderId once the user
        // has actually navigated into a real subfolder.
        folderId={
          targetUploadFolderId ||
          (currentFolderId && currentFolderId !== rootFolderId ? currentFolderId : null)
        }
        maxFileSize={maxFileSize}
        maxFiles={maxFiles}
        onUploadSuccess={async () => {
          setDroppedFiles([]);
          setTargetUploadFolderId(null);
          setTargetUploadFolderName("");
          refetch();
          if (onEntityUpdated) await onEntityUpdated(activeEntityId);
          if (typeof refreshProgressJourney === "function") refreshProgressJourney();
          if (activeEntityId && entityType === DOCUMENT_ENTITY_TYPES.LEAD) {
            invalidateTimelineCache(activeEntityId);
          }
        }}
      />

      {/* Move Item Modal */}
      {isMoveModalOpen && (
        <MoveItemModal
          isOpen={isMoveModalOpen}
          onClose={() => {
            setIsMoveModalOpen(false);
            setItemsToMoveTarget([]);
          }}
          itemsToMove={itemsToMoveTarget}
          entityType={entityType}
          entityId={activeEntityId}
          rootFolderId={rootFolderId}
          currentFolderId={currentFolderId}
          onSuccess={async () => {
            clearSelection();
            await refetch(true);
            if (onEntityUpdated) await onEntityUpdated(activeEntityId);
            if (typeof refreshProgressJourney === "function") refreshProgressJourney();
            if (activeEntityId && entityType === DOCUMENT_ENTITY_TYPES.LEAD) {
              invalidateTimelineCache(activeEntityId);
            }
          }}
        />
      )}

      {/* Verification Modal for Lead */}
      {features.enableVerification && (
        <DocumentVerificationModal
          isOpen={isVerificationModalOpen}
          onClose={handleCloseVerificationModal}
          document={verificationDocument}
          action={verificationAction}
          onSubmit={handleSubmitVerification}
          isSubmitting={isUpdatingVerification}
        />
      )}
    </div>
  );
};

export default DocumentManager;
